import {
	type Price,
	type Channel,
	type LineItem,
} from '@commercetools/platform-sdk'
import {
	type DataFunctionArgs,
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
	defer,
	redirect,
} from '@remix-run/node'
import { isPrefetch } from 'remix-utils/is-prefetch'
import { ShippingKeys } from '#app/constants/shipping-keys'
import { createToastHeaders } from '#app/cookies/toast.server'
import {
	getUserPreferences,
	getUserPreferencesAndHeader,
} from '#app/cookies/user-preferences.server'
import ProductDetailsPage from '#app/features/pdp'
import {
	getSimilarClerkProducts,
	getSpecificBrandClerkProducts,
} from '#app/server/clerk/pdp.server'
import { addLineItem } from '#app/server/commerce-tools/cart/addLineItem.server'
import { getCart } from '#app/server/commerce-tools/cart/getCart.server'
import {
	fetchProductData,
	getComplementaryCTProducts,
	getProductProjectionsSearch,
} from '#app/server/commerce-tools/product/product.server'
import {
	getStoreByKey,
	getStores,
} from '#app/server/commerce-tools/store/store.server'
import { getOutfits } from '#app/server/partner.server.ts'
import { makeTimings, time } from '#app/server/utils/timing.server'
import { AddUtmParameters } from '#app/utils/add-utm-params'
import { languageSession } from '#app/utils/localisation/lang.server'
import {
	getLanguageFromCookieOrBrowserOrFallback,
	getLokalisedString,
} from '#app/utils/localisation/lokalisation-utilities'
import { getFirstNonInstagramOrCustomMedia } from '#app/utils/media-utils'
import { combineHeaders } from '#app/utils/misc'
interface IChannel extends Channel {
	storeKey: string
}

export let handle = { i18n: 'product_details_page' }

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
	const timings = makeTimings('pdp loader')

	const { styleNumber } = params

	if (!styleNumber) return redirect('404-not-found')
	let headers = new Headers()
	if (isPrefetch(request)) {
		headers.set('Cache-Control', 'private, max-age=30, smax-age=0')
	}
	const url = new URL(request.url)
	const storeParams = url.searchParams.get('store')?.split(',') || []
	const styleParam = url.searchParams.get('style')
	const skuParam = url.searchParams.get('sku')
	const userPreferences = await getUserPreferences(request)

	const outfitsByStyle = await time(
		() => getOutfits(request, styleNumber, undefined, undefined, 'newest'),
		{
			timings,
			type: 'get outfits',
			desc: 'getOutfits in pdp',
		},
	)
	const stores = await time(() => getStores(), {
		timings,
		type: 'get stores',
		desc: 'getStores in pdp',
	})

	if (
		// handling the case when the user has no selected stores in the cookie but the store param is not empty
		(!userPreferences?.selectedStores ||
			userPreferences?.selectedStores?.length === 0) &&
		storeParams.length > 0
	) {
		const storesFromURL = stores
			.filter(sc => storeParams.includes(sc?.key || ''))
			.map(sc => ({
				key: sc?.distributionChannels[0].obj?.key,
				name: sc?.distributionChannels[0].obj?.name?.en,
			}))

		const { headers } = await getUserPreferencesAndHeader(request, {
			selectedStores: storesFromURL,
		})

		return redirect(`/product/${styleNumber}?${url.searchParams.toString()}`, {
			headers: combineHeaders(headers),
		})
	} else if (
		userPreferences?.selectedStores?.length > 0 &&
		storeParams.length > 0
	) {
		const storesFromURL = stores
			.filter(sc => storeParams.includes(sc?.key || ''))
			.map(sc => ({
				key: sc?.distributionChannels[0].obj?.key,
				name: sc?.distributionChannels[0].obj?.name?.en,
			}))

		// compare the selected stores in the cookie with the ones in the url
		if (
			storesFromURL.some(
				storeFromURL =>
					!userPreferences.selectedStores.some(
						(storeFromCookie: any) => storeFromCookie.key === storeFromURL.key,
					),
			)
		) {
			const { headers } = await getUserPreferencesAndHeader(request, {
				selectedStores: storesFromURL,
			})

			return redirect(
				`/product/${styleNumber}?${url.searchParams.toString()}`,
				{
					headers: combineHeaders(headers),
				},
			)
		}
	}

	const langSession = await languageSession.getSession(
		request.headers.get('cookie'),
	)

	const language: string = getLanguageFromCookieOrBrowserOrFallback(
		langSession.get('sessionLanguage'),
		request,
	)
	const locale = language.split('-')[0] === 'en' ? 'en-US' : 'da'

	const currentUrl = url.toString()
	const productData = await time(
		() => fetchProductData(styleNumber, styleParam, storeParams),
		{
			timings,
			type: 'get product data',
			desc: 'fetchProductData in pdp',
		},
	)
	if (!productData) return redirect('/404-not-found')

	const storeChannels = stores.map(store => {
		return {
			...store.distributionChannels[0].obj,
			storeKey: store.key,
		} as IChannel
	})

	const newChannels = storeChannels

	const category = productData?.productInfo.categories[0].obj?.key // Assuming this is how you access the brand in your product data structure
	const gender = category?.split('-')[0]
	const brand = productData?.brand?.value
	const complementProducts =
		productData?.productInfo.masterVariant?.attributes?.find(
			at => at.name === 'complement_products',
		)?.value || {}

	let newComplementProducts = {}
	if (complementProducts) {
		try {
			newComplementProducts = JSON.parse(complementProducts)
		} catch (error) {
			newComplementProducts = {}
		}
	}
	// do this async
	const complementProductsArray = await time(
		() => getComplementaryCTProducts(newComplementProducts),
		{
			timings,
			type: 'get complementary products',
			desc: 'get complementary CT products in pdp',
		},
	)

	const similarClerkProductData = await time(
		() => getSimilarClerkProducts(request, category, styleNumber),
		{
			timings,
			type: 'get similar clerk products',
			desc: 'getSimilarClerkProducts in pdp',
		},
	)
	const specificBrandClerkProducts = await time(
		() => getSpecificBrandClerkProducts(request, gender, brand),
		{
			timings,
			type: 'get specific brand clerk products',
			desc: 'getSpecificBrandClerkProducts in pdp',
		},
	)
	headers.set('Server-Timing', timings.toString())
	return defer(
		{
			storesChannels: newChannels as IChannel[],
			similarClerkProductData,
			specificBrandClerkProducts,
			complementProductsArray,
			productData,
			currentUrl,
			storeParams,
			skuParam,
			outfitsByStyle,
			locale,
		},
		{ headers: headers },
	)
}

export const action = async ({ request }: DataFunctionArgs) => {
	const oldCart = await getCart(request)
	if (!oldCart)
		return json({ status: 'error', newItems: [], newDistributionChannel: null })
	let formData = await request.formData()
	const userPreferences = await getUserPreferences(request)
	const sku = formData.get('sku')
	const styleNumber = formData.get('styleNumber')
	const storeKey = formData.get('storeKey') as string
	const channelId = formData.get('channelId') as any

	const queryArgs = {
		filter: [`key:"${styleNumber}"`],
		staged: false,
		markMatchingVariants: true,
		expand: [
			'categories[*].ancestors[*]',
			'variants[*].prices[*].channel',
			'masterVariant.prices[*].channel',
			'variants[*].prices[*].discounted.discount.obj',
			'masterVariant.prices[*].discounted.discount.obj',
		],
	}
	if (sku) {
		queryArgs.filter.push(`variants.sku:"${sku}"`)
	}

	const productResponse = await getProductProjectionsSearch(queryArgs)
	const product = productResponse[0]
	const allVariants = [product.masterVariant, ...product.variants]

	const selectedVariant = allVariants.find(variant => variant.isMatchingVariant)

	if (!selectedVariant) {
		return json({ status: 'error', newItems: [], newDistributionChannel: null })
	}

	let selectedChannelId = channelId // Use the provided channelId if available
	// This regex validates a UUID (Universally Unique Identifier) in its standard 8-4-4-4-12 format.
	// Breakdown:
	//   ^                : Start of string
	//   [0-9a-f]{8}      : Exactly 8 hexadecimal characters (0-9 or a-f)
	//   -                : A hyphen separator
	//   [0-9a-f]{4}      : Exactly 4 hexadecimal characters
	//   -                : A hyphen separator
	//   [0-9a-f]{4}      : Exactly 4 hexadecimal characters
	//   -                : A hyphen separator
	//   [0-9a-f]{4}      : Exactly 4 hexadecimal characters
	//   -                : A hyphen separator
	//   [0-9a-f]{12}     : Exactly 12 hexadecimal characters
	//   $                : End of string
	//   i                : Case-insensitive flag (matches both uppercase and lowercase letters)
	//
	// This ensures that the entire string matches the UUID pattern and nothing extra.
	// Yes chatgpt made the regex and the breakdown for me.
	const uuidRegex =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
	if (!selectedChannelId || !uuidRegex.test(selectedChannelId)) {
		if (storeKey) {
			const selectedStore = await getStoreByKey(storeKey)
			selectedChannelId = selectedStore?.distributionChannels[0]?.id
		}
	}

	if (!selectedChannelId) {
		const lineItemChannelId =
			findChannelWithPriority(
				selectedVariant.availability?.channels as any,
				selectedVariant.prices || [],
				oldCart.lineItems,
			) ?? ''
		selectedChannelId = lineItemChannelId
	}

	if (!selectedChannelId) {
		return json(
			{ status: 'error', newItems: [], newDistributionChannel: null },
			{
				headers: await createToastHeaders({
					description: 'Something went wrong',
					type: 'error',
				}),
			},
		)
	}

	let shippingMethod
	if (channelId) {
		shippingMethod = ShippingKeys.Pickup
	} else {
		const currentShipping = oldCart.shipping.find(
			(it: any) => it.shippingKey !== 'defaultPickupShippingMethod',
		)
		shippingMethod = currentShipping?.shippingKey.includes(ShippingKeys.Parcel)
			? ShippingKeys.Parcel
			: ShippingKeys.Delivery
	}

	const response = await addLineItem(
		request,
		product.id,
		selectedVariant.id,
		selectedChannelId,
		1,
		shippingMethod,
	)

	const newItems = response?.body.lineItems.filter(item => {
		return !oldCart.lineItems.find(lineItem => {
			return lineItem.id === item.id && lineItem.quantity === item.quantity
		})
	})

	// we need to update the selected store in the user preferences just if the user has not selected any store yet

	if (
		userPreferences?.selectedStores?.length === 0 ||
		!userPreferences?.selectedStores
	) {
		const newDistributionChannel = newItems?.[0]?.distributionChannel?.obj

		const selectedDC = {
			name: newDistributionChannel?.name?.en,
			key: newDistributionChannel?.key,
		}
		const { headers } = await getUserPreferencesAndHeader(request, {
			selectedStores: [selectedDC],
		})
		return json(
			{ status: 'success', newItems, newDistributionChannel },
			{
				headers: combineHeaders(headers),
			},
		)
	}

	return json({ status: 'success', newItems, newDistributionChannel: null })
}

const getEffectivePriceValue = (price: Price): number => {
	return price.discounted
		? price.discounted.value.centAmount
		: price.value.centAmount
}

export const findChannelWithPriority = (
	channels: {
		[key: string]: { availableQuantity: number; id: string }
	},
	prices: Price[],
	lineItems: LineItem[],
): string | null => {
	let highestStockChannelId: string | null = null
	let highestStock = 0
	let isSameStock = true
	let firstChannel = true
	let firstStock = 0

	// Step A: Find the price channel with the *lowest* price among all Prices.
	// If there are no Prices at all, we skip this step.
	let lowestPrice: Price | undefined
	let lowestPriceValue = Infinity

	prices.forEach(price => {
		const priceValue = getEffectivePriceValue(price)
		if (priceValue < lowestPriceValue) {
			lowestPriceValue = priceValue
			lowestPrice = price
		}
	})

	// Step B: If we found a price channel with the lowest price, and it has a channel:
	// - If that channel exists in 'channels' and has enough stock, just return it.
	//   (You can define your own threshold logic here.)
	if (lowestPrice?.channel?.id) {
		const lowestPriceChannelId = lowestPrice.channel.id
		const channelData = lowestPriceChannelId && channels[lowestPriceChannelId]

		if (channelData) {
			// Example threshold: must have more than 0 in stock
			if (channelData.availableQuantity > 0) {
				return lowestPriceChannelId
			}
		}
	}

	// ---------------------------------------
	// From here on, fallback to your original
	// "highest stock + tie-breaking" logic
	// ---------------------------------------

	// Gather the IDs of channels already used in the cart
	const allExistingChannelIdsInCart = lineItems
		.map(item => item.distributionChannel?.id)
		.filter((id): id is string => id !== undefined)

	// 1) Loop to determine highest stock & if all channels have same stock
	for (const key in channels) {
		const currentStock = channels[key].availableQuantity

		if (firstChannel) {
			firstStock = currentStock
			firstChannel = false
		} else if (currentStock !== firstStock) {
			isSameStock = false
		}

		if (currentStock > highestStock) {
			highestStock = currentStock
			highestStockChannelId = key
		}
	}

	// 2) If any channel already in cart is "close enough" to highest stock, return it
	for (const pickedChannelId of allExistingChannelIdsInCart) {
		if (
			pickedChannelId &&
			channels[pickedChannelId] &&
			channels[pickedChannelId].availableQuantity > 1 &&
			channels[pickedChannelId].availableQuantity >= highestStock - 2
		) {
			return pickedChannelId
		}
	}

	// 3) If *all* channels have the same stock, pick a channel from the cart or random
	if (isSameStock) {
		for (const pickedChannelId of allExistingChannelIdsInCart) {
			if (pickedChannelId && channels[pickedChannelId]) {
				return pickedChannelId
			}
		}

		const channelKeys = Object.keys(channels)
		const randomChannel =
			channelKeys[Math.floor(Math.random() * channelKeys.length)]
		return randomChannel
	}

	// 4) Otherwise, return the channel with the highest stock
	return highestStockChannelId
}

// TODO: add twitter and locale
export const meta: MetaFunction<typeof loader> = ({ data }) => {
	// Extract placement from URL or some other mechanism

	const currentUrlWithUtm = AddUtmParameters(
		data?.currentUrl,
		'social',
		'plentyand_dk_meta_shopping',
		'meta_catalog', // Always set utm_source to meta_catalog
	)
	const locale = data?.locale
	if (!locale) return []

	const canonicalURL = data?.currentUrl.split('?')[0]
	const isDiscounted = Boolean(
		data?.productData?.productInfo.masterVariant.prices?.[0]?.discounted?.value,
	)

	const price = isDiscounted
		? data?.productData?.productInfo.masterVariant.prices?.[0]?.discounted
				?.value.centAmount
		: data?.productData?.productInfo.masterVariant.prices?.[0]?.value.centAmount
	const sku = data?.productData?.productInfo.masterVariant.sku
	const formattedPrice = `${(price! / 100).toFixed(2)}`

	const availability = data?.productData?.styleAvailableVariants.some(
		(variant: { isOnStock: any }) => variant.isOnStock,
	)
		? 'in stock'
		: 'out of stock'
	const availabilitySchema = data?.productData?.styleAvailableVariants.some(
		variant => variant.isOnStock,
	)
		? 'https://schema.org/InStock'
		: 'https://schema.org/OutOfStock'

	const brand = data?.productData?.brand?.value
	const name = data?.productData?.name

	const title = `${brand} - ${name}`

	let description = getLokalisedString(
		data?.productData?.productInfo.masterVariant.attributes?.find(
			attr => attr.name === 'description',
		)?.value,
		locale,
	).slice(0, 160)

	if (description.length === 160) {
		description += '...'
	}
	const image = getFirstNonInstagramOrCustomMedia(
		data?.productData?.productInfo.masterVariant.images,
	)

	const codeName =
		data?.productData?.productInfo.masterVariant.attributes?.find(
			attr => attr.name === 'code_name',
		)?.value

	const color = data?.productData?.productInfo.masterVariant.attributes?.find(
		attr => attr.name === 'color',
	)?.value
	const mainColor =
		data?.productData?.productInfo.masterVariant.attributes?.find(
			attr => attr.name === 'main_color',
		)?.value

	const translatedColor =
		getLokalisedString(color, locale) ||
		getLokalisedString(mainColor, locale) ||
		'Ingen farve'

	return [
		{
			title,
		},
		{
			name: 'description',
			content: description,
		},
		{ tagName: 'link', rel: 'canonical', href: canonicalURL },
		{
			name: 'name',
			content: `${name} | ${codeName}`,
		},
		{ name: 'style-number', content: `${data?.productData?.productInfo.key}` },
		{
			name: 'code-name',
			content: codeName,
		},
		{
			name: 'brand',
			content: `${data?.productData?.brand?.value}`,
		},
		{
			name: 'color',
			content: translatedColor,
		},
		// Open Graph Meta fields
		{
			property: 'og:title',
			content: title,
		},
		{
			property: 'og:site_name',
			content: `Plenty&`,
		},
		{
			property: 'og:site',
			content: `plentyand.dk`,
		},
		{
			property: 'og:description',
			content: description,
		},
		{
			property: 'og:type',
			content: `product`,
		},
		{
			property: 'og:image',
			content: image,
		},
		{
			property: 'og:price:amount',
			content: formattedPrice,
		},
		{
			property: 'og:price:currency',
			content: 'DKK',
		},
		{
			property: 'og:availability',
			content: availability,
		},
		{
			property: 'og:url',
			content: currentUrlWithUtm,
		},
		{
			property: 'product:retailer_item_id',
			content: data?.productData.productInfo.id,
		},
		{
			property: 'og:type',
			content: `plenty_sharing:product`,
		},
		// Twitter

		// generate the twitter metacards
		{
			property: 'twitter:card',
			content: 'summary_large_image',
		},
		{
			property: 'twitter:title',
			content: title,
		},
		{
			property: 'twitter:description',
			content: description,
		},
		{
			property: 'twitter:image',
			content: image,
		},
		// Schema.org
		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'Product',
				content: `${name} | ${codeName}`,
				image: image,
				description: description,
				sku: sku,
				gtin: sku,
				color: translatedColor,
				brand: {
					'@type': 'Brand',
					name: brand,
				},
				mpn: codeName,
				offers: {
					'@type': 'Offer',
					url: data?.currentUrl,
					price: formattedPrice,
					priceCurrency: 'DKK',
					availability: availabilitySchema,
					seller: {
						'@type': 'Organization',
						name: 'Plenty&',
					},
				},
			},
		},
	]
}

export default function () {
	return <ProductDetailsPage />
}
